import designSl from '@/design/i18n/sl'

/*
 * Slovenščina — the console's first language, and the SCHEMA the other one is checked
 * against: `en.ts` is `satisfies MessageSchema`, so a key missing from it (or one only it has)
 * fails `yarn type-check`. Plural messages carry the four Slovenian forms (1, 2, 3–4, 5+) in
 * that order; `libs/i18n/index.ts` picks the right one. The labels of the API's enums are the
 * contract's (docs/API.md → UI labels).
 */
const sl = {
    general: {
        add:      'Dodaj',
        edit:     'Uredi',
        delete:   'Izbriši',
        cancel:   'Prekliči',
        save:     'Shrani',
        close:    'Zapri',
        confirm:  'Potrdi',
        search:   'Išči …',
        loading:  'Nalagam …',
        saving:   'Shranjujem',
        sign_out: 'Odjava',
        open:     'Odpri',
        actions:  'Možnosti',
        none:     '—',
    },
    brand: {
        product_name: 'Imprion DS',
        tagline:      'Zasloni · Vsebine · Urniki',
        provided_by:  'Ponudnik',
    },
    nav: {
        overview:       'Pregled',
        screens:        'Zasloni',
        media:          'Vsebine',
        templates:      'Predstavitve',
        schedules:      'Urniki',
        brand:          'Znamka',
        menu:           'Meni',
        tenant_users:   'Uporabniki',
        group_provider: 'Ponudnik',
        tenants:        'Stranke',
        users:          'Uporabniki',
        settings:       'Nastavitve',
    },
    layout: {
        open_menu:  'Odpri meni',
        close_menu: 'Zapri meni',
    },
    color_mode: {
        switch_to_light: 'Preklopi na svetli način',
        switch_to_dark:  'Preklopi na temni način',
    },
    context: {
        label:       'Prostor stranke',
        help:        'Nastavitve se uporabljajo za izbrani prostor.',
        placeholder: 'Izberite stranko',
        inactive:    '{name} (izklopljena)',
        not_found:   'Tega prostora stranke ni mogoče odpreti.',
    },
    role: {
        admin:          'Skrbnik',
        editor:         'Urednik',
        provider_admin: 'Skrbnik ponudnika',
    },
    locale: {
        label: 'Jezik',
        sl:    'Slovenščina',
        en:    'English',
    },
    errors: {
        generic_error:       'Prišlo je do napake.',
        invalid_data:        'Podatki niso veljavni.',
        not_found:           'Ne obstaja (več).',
        unauthorized:        'Prijava je potekla. Prijavite se znova.',
        forbidden:           'Za to nimate dovoljenja.',
        on_add:              'Dodajanje ni uspelo.',
        on_edit:             'Shranjevanje ni uspelo.',
        on_delete:           'Brisanje ni uspelo.',
        on_load:             'Nalaganje ni uspelo.',
        tenant_inactive:     'Prostor stranke je izklopljen — prijava ni mogoča.',
        weather_unavailable: 'Vremenska napoved trenutno ni dosegljiva.',
        in_use:              'Še je v uporabi, zato je ni mogoče izbrisati.',
        in_use_list:         'Še je v uporabi, zato je ni mogoče izbrisati: {list}',
        pos_not_configured:  'Blagajna ni povezana — skrbnik objekta mora nastaviti okolje, žeton in poslovno enoto.',
        pos_failed:          'Blagajna Imprion POS ni odgovorila ali je zahtevo zavrnila.',
        pos_failed_reason:   'Blagajna Imprion POS je zahtevo zavrnila: {reason}',
        share_code_invalid:  'Koda ne obstaja ali deljenje na tem zaslonu ni vklopljeno.',
        share_session_ended: 'Deljenje je že končano.',
        domain_taken:        'Ta naslov za deljenje že uporablja drug videz ali spletni naslov stranke.',
    },
    success: {
        add:    'Dodano.',
        edit:   'Shranjeno.',
        delete: 'Izbrisano.',
    },
    validation: {
        required: 'Obvezno polje.',
        email:    'Neveljaven e-poštni naslov.',
        too_long: 'Predolgo.',
        invalid:  'Neveljavna vrednost.',
    },
    date: {
        pick:  'Izberi datum',
        clear: 'Počisti datum',
    },
    time: {
        today_at:    'danes {time}',
        tomorrow_at: 'jutri {time}',
    },
    auth: {
        username:          'Uporabniško ime',
        password:          'Geslo',
        password_show:     'Pokaži geslo',
        password_hide:     'Skrij geslo',
        sign_in:           'Prijava',
        or:                'ali',
        failed:            'Napačno uporabniško ime ali geslo.',
        too_many_attempts: 'Preveč neuspelih poskusov. Poskusite znova čez nekaj minut.',
        welcome:           'Prijavite se v svoj prostor.',
    },
    passkey: {
        passkeys:        'Ključi za prijavo (passkeys)',
        subtitle:        'Prijava s prstnim odtisom, obrazom ali varnostnim ključem namesto gesla.',
        sign_in:         'Prijava s passkeyem',
        sign_in_failed:  'Prijava s passkeyem ni uspela.',
        add:             'Dodaj passkey',
        added:           'Passkey je dodan.',
        failed:          'Passkeya ni bilo mogoče dodati.',
        name:            'Ime',
        name_help:       'Po imenu boste ključ prepoznali na seznamu, npr. »Službeni prenosnik«.',
        default_name:    'Moja naprava',
        continue:        'Nadaljuj',
        created:         'Dodan',
        last_used:       'Zadnja uporaba',
        never_used:      'Še ni bil uporabljen',
        empty:           'Nimate še nobenega passkeya.',
        delete:          'Izbriši passkey',
        confirm_delete:  'Izbrišem passkey »{name}«? S to napravo se ne boste več mogli prijaviti brez gesla.',
        removed:         'Passkey je izbrisan.',
        delete_failed:   'Passkeya ni bilo mogoče izbrisati.',
        unavailable:     'Prijava s passkeyi na tem strežniku ni nastavljena.',
        unsupported:     'Ta brskalnik ne podpira passkeyev (potrebna je varna povezava HTTPS).',
    },
    password: {
        change:        'Spremeni geslo',
        current:       'Trenutno geslo',
        new:           'Novo geslo',
        repeat:        'Ponovite novo geslo',
        mismatch:      'Gesli se ne ujemata.',
        changed:       'Geslo je spremenjeno.',
        failed:        'Gesla ni bilo mogoče spremeniti.',
        wrong_current: 'Trenutno geslo ni pravilno.',
    },
    settings: {
        title:             'Nastavitve',
        subtitle:          'Vaš račun, jezik in prijava.',
        profile:           'Profil',
        profile_subtitle:  'Ime in e-poštni naslov vašega računa.',
        account:           'Račun',
        tenant:            'Prostor stranke',
        tenants:           'Prostori strank',
        role:              'Vloga',
        locale_help:       'Jezik aplikacije.',
        provider:          'Ponudnik',
        provider_subtitle: 'Ime ponudnika, ki ga zasloni pokažejo, kadar ima stranka vklopljeno znamko ponudnika.',
        provider_name:     'Ime ponudnika',
        version:           'Različica',
    },
    user: {
        users:               'Uporabniki',
        subtitle_tenant:     'Kdo se lahko prijavi v prostor stranke in kaj sme urejati.',
        subtitle_provider:   'Uporabniki vseh strank in skrbniki ponudnika.',
        add:                 'Nov uporabnik',
        edit:                'Uredi uporabnika',
        delete:              'Izbriši uporabnika',
        name:                'Ime in priimek',
        username:            'Uporabniško ime',
        username_rule:       '3–32 znakov: male črke, številke, pika, podčrtaj ali vezaj.',
        username_fixed:      'Uporabniškega imena ni mogoče spremeniti.',
        username_taken:      'To uporabniško ime že obstaja.',
        email:               'E-pošta',
        role:                'Vloga',
        role_help:           'Urednik ureja vsebine, predstavitve in urnike; skrbnik še zaslone, znamko in uporabnike.',
        space:               'Prostor',
        tenants:             'Prostori',
        other_tenants:       'Drugi prostori',
        no_memberships:      'Brez prostora',
        filter_all:          'Vsi prostori',
        filter_provider:     'Skrbniki ponudnika',
        password:            'Geslo',
        password_reset:      'Novo geslo (ponastavitev)',
        password_keep:       'Pustite prazno, da geslo ostane nespremenjeno.',
        password_rule:       'Najmanj 8 znakov.',
        password_too_short:  'Geslo mora imeti najmanj 8 znakov.',
        active:              'Aktiven',
        active_help:         'Neaktiven uporabnik se ne more prijaviti.',
        status:              'Stanje',
        status_active:       'Aktiven',
        status_inactive:     'Neaktiven',
        last_login:          'Zadnja prijava',
        never_logged_in:     'Še nikoli',
        search:              'Išči po imenu ali e-pošti …',
        show_inactive:       'Pokaži neaktivne',
        empty:               'Ni uporabnikov.',
        you:                 'Vi',
        cannot_delete_self:  'Svojega računa ne morete izbrisati.',
        confirm_delete:      'Izbrišem uporabnika {name}? Račun se izbriše v vseh prostorih in se ne bo mogel več prijaviti.',
        deleted:             'Uporabnik je izbrisan.',
        delete_failed:       'Uporabnika ni bilo mogoče izbrisati.',
        last_admin:          'To je zadnji aktivni skrbnik — ostati mora vsaj eden.',
        locale:              'Jezik',
        locale_help:         'Jezik aplikacije za tega uporabnika.',
        role_in_tenant:      'Vloga v tem prostoru',
        not_manageable:      'Uporabnik je tudi v drugih prostorih — osebne podatke ureja ponudnik.',
        remove:              'Odstrani iz prostora',
        confirm_remove:      'Odstranim uporabnika {name} iz tega prostora? Če je član še drugih prostorov, njegov račun tam ostane; sicer se ne bo mogel več prijaviti.',
        removed:             'Uporabnik je odstranjen iz prostora.',
        remove_failed:       'Uporabnika ni bilo mogoče odstraniti iz prostora.',
        cannot_remove_self:  'Sebe ne morete odstraniti iz prostora.',
        add_existing:        'Dodaj obstoječega',
        add_existing_title:  'Dodaj obstoječega uporabnika',
        add_existing_help:   'Točno uporabniško ime osebe, ki že ima račun v drugem prostoru.',
        add_existing_action: 'Dodaj v prostor',
        exists_offer:        'Uporabnik s tem imenom že obstaja. Dodaj obstoječega uporabnika v ta prostor?',
        member_added:        'Uporabnik je dodan v prostor.',
        member_not_found:    'Aktivnega uporabnika s tem uporabniškim imenom ni.',
        already_member:      'Ta uporabnik je že član tega prostora.',
        provider_admin:      'Skrbnik ponudnika',
        provider_admin_help: 'Upravlja vse stranke, uporabnike in nastavitve ponudnika; v vsakem prostoru ima pravice skrbnika.',
        memberships:         'Prostori',
        memberships_help:    'V katerih prostorih strank uporabnik dela in s katero vlogo.',
        membership_add:      'Dodaj prostor',
        membership_remove:   'Odstrani prostor',
        memberships_none:    'Uporabnik še ni v nobenem prostoru.',
        memberships_required: 'Dodajte vsaj en prostor ali vklopite skrbnika ponudnika.',
        memberships_duplicate: 'Vsak prostor je lahko naveden le enkrat.',
    },
    tenant: {
        tenants:         'Stranke',
        subtitle:        'Vsaka stranka ima svoj prostor: zaslone, vsebine, urnike, znamko in uporabnike.',
        add:             'Nova stranka',
        edit:            'Uredi stranko',
        delete:          'Izbriši stranko',
        name:            'Ime',
        name_placeholder: 'Hotel Lipa',
        slug:            'Oznaka',
        slug_help:       'Male črke, številke in vezaj (2–40 znakov). Nastane iz imena.',
        slug_rule:       'Samo male črke, številke in vezaj, 2–40 znakov.',
        slug_taken:      'To oznako že uporablja druga stranka.',
        timezone:        'Časovni pas',
        timezone_help:   'Po tem času se izvajajo urniki na zaslonih.',
        locale:          'Jezik',
        location:        'Lokacija',
        location_help:   'Privzeta lokacija za vreme na zaslonih stranke.',
        active:          'Aktivna',
        active_help:     'Izklopljena stranka se ne more prijaviti, njeni zasloni ne dobijo vsebin.',
        status:          'Stanje',
        status_active:   'Aktivna',
        status_inactive: 'Izklopljena',
        screens:         'Zasloni',
        users:           'Uporabniki',
        created:         'Ustvarjena',
        open:            'Odpri prostor',
        confirm_delete:  'Izbrišem stranko {name}? Njeni uporabniki se ne bodo mogli več prijaviti, njeni zasloni ugasnejo.',
        deleted:         'Stranka je izbrisana.',
        delete_failed:   'Stranke ni bilo mogoče izbrisati.',
        empty:           'Ni še nobene stranke.',
        empty_help:      'Dodajte prvo stranko, nato odprite njen prostor.',
        search:          'Išči po imenu ali oznaki …',
        activated:       'Stranka {name} je vklopljena.',
        deactivated:     'Stranka {name} je izklopljena.',
    },
    location: {
        label:             'Kraj',
        label_placeholder: 'Ljubljana',
        lat:               'Zemljepisna širina',
        lon:               'Zemljepisna dolžina',
        lat_range:         'Med −90 in 90.',
        lon_range:         'Med −180 in 180.',
    },
    orientation: {
        landscape: 'Ležeče',
        portrait:  'Pokončno',
        ratio:     '{orientation} ({ratio})',
    },
    status: {
        online:   'Na zvezi',
        offline:  'Brez zveze',
        unpaired: 'Ni seznanjen',
    },
    overview: {
        title:             'Pregled',
        subtitle:          'Stanje zaslonov in objav v prostoru stranke.',
        online:            'Na zvezi',
        offline:           'Brez zveze',
        unpaired:          'Neseznanjeni',
        unpublished:       'Objave s spremembami',
        unpublished_of:    'od {total} postavitev',
        active_rules:      'Aktivna pravila',
        media:             'Prostor medijev',
        media_count:       '{n} datoteka | {n} datoteki | {n} datoteke | {n} datotek',
        screens:           'Zasloni',
        all_screens:       'Vsi zasloni',
        no_screens:        'Ni še nobenega zaslona.',
        now_playing:       'Predvaja',
        nothing_playing:   'Nič',
        last_seen:         'Nazadnje',
        never_seen:        'Še nikoli',
        upcoming:          'Prihajajoče',
        upcoming_subtitle: 'Začetki pravil v naslednjih 24 urah.',
        no_upcoming:       'V naslednjih 24 urah se ne začne nobeno pravilo.',
        all_rules:         'Vsi urniki',
    },
    diagnostics: {
        title:       'Diagnostika',
        version:     'Različica predvajalnika',
        resolution:  'Ločljivost',
        user_agent:  'Naprava (user agent)',
        log:         'Dnevnik naprave',
        log_help:    'Kar je prikazovalnik sam sporočil: napake, neuspeli prenosi, menjave strani. Hrani se 7 dni.',
        refresh:     'Osveži',
        details:     'Podrobnosti',
        empty:       'Naprava še ni sporočila ničesar.',
        empty_help:  'Vnosi se pojavijo, ko prikazovalnik odpre predvajalnik različice 1.1.0 ali novejše.',
        level_error: 'Napaka',
        level_warn:  'Opozorilo',
        level_info:  'Obvestilo',
    },
    screen: {
        screens:                'Zasloni',
        subtitle:               'Prikazovalniki v prostoru stranke, njihove skupine in osnovna vsebina.',
        add:                    'Dodaj zaslon',
        edit:                   'Uredi zaslon',
        delete:                 'Izbriši zaslon',
        name:                   'Ime',
        name_placeholder:       'Recepcija',
        group:                  'Skupina',
        no_group:               'Brez skupine',
        orientation:            'Usmerjenost',
        location:               'Lokacija za vreme',
        location_tenant:        'Lokacija stranke',
        location_custom:        'Lastna lokacija zaslona',
        default_design:         'Osnovna vsebina',
        default_design_help:    'Kaj zaslon prikazuje, ko ne velja nobeno pravilo: postavitev ali zaporedje.',
        default_none:           'Zaslon znamke (logotip)',
        pairing_code:           'Koda za seznanitev',
        pairing_code_help:      'Neobvezno: koda, ki jo prikazuje zaslon. Seznanite ga lahko tudi pozneje.',
        pair:                   'Seznani',
        pair_title:             'Seznani zaslon {name}',
        pair_help:              'Vpišite kodo, ki jo prikazuje zaslon.',
        pair_code_rule:         'Koda ima 6 znakov, npr. K7M-4QX.',
        paired:                 'Zaslon je seznanjen.',
        pair_invalid:           'Koda ne obstaja, je potekla ali je že uporabljena.',
        repair:                 'Seznani znova',
        repair_help:            'Stari prikazovalnik bo s tem odklopljen.',
        unpair:                 'Odklopi',
        confirm_unpair:         'Odklopim zaslon {name}? Prikazovalnik bo znova pokazal kodo za seznanitev.',
        unpaired_done:          'Zaslon je odklopljen.',
        confirm_delete:         'Izbrišem zaslon {name}? Pravila ga ne bodo več ciljala.',
        deleted:                'Zaslon je izbrisan.',
        connect_title:          'Kako povežem zaslon?',
        connect_hint:           'Na zaslonu odprite {url} in vpišite prikazano kodo.',
        now_playing:            'Predvaja',
        last_seen:              'Nazadnje na zvezi',
        never_seen:             'Še nikoli',
        empty:                  'Ni še nobenega zaslona.',
        empty_help:             'Dodajte zaslon, nato ga seznanite s kodo, ki jo pokaže prikazovalnik.',
        empty_filtered:         'V tej skupini ni zaslonov.',
        filter_all:             'Vse skupine',
        groups:                 'Skupine',
        groups_subtitle:        'Skupina združi zaslone, ki jih pravila ciljajo skupaj, npr. »Recepcija + lobby«.',
        group_add:              'Dodaj skupino',
        group_name:             'Ime skupine',
        group_name_placeholder: 'Recepcija + lobby',
        group_count:            '{n} zaslon | {n} zaslona | {n} zasloni | {n} zaslonov',
        group_confirm_delete:   'Izbrišem skupino {name}? Njeni zasloni ostanejo brez skupine.',
        group_deleted:          'Skupina je izbrisana.',
        groups_empty:           'Ni še nobene skupine.',
        read_only:              'Zaslone ureja skrbnik stranke.',
        count:                  '{n} zaslon | {n} zaslona | {n} zasloni | {n} zaslonov',
        share:                  'Deljenje zaslona',
        share_help:             'Zaslon prikaže kodo za deljenje, prenosnik pa nanj deli svoj zaslon na {url}. Deluje z aplikacijo Imprion DS na Apple TV — spletni predvajalnik deljenja ne sprejema.',
        share_on:               'Deljenje zaslona je vklopljeno',
    },
    share: {
        title:                 'Deli zaslon',
        subtitle:              'Pokažite, kar je na vašem računalniku, na zaslonu v prostoru.',
        code:                  'Koda z zaslona',
        code_help:             'Koda je v spodnjem desnem kotu zaslona, npr. K7M-4QX.',
        code_rule:             'Koda ima 6 znakov, npr. K7M-4QX.',
        name:                  'Vaše ime',
        name_help:             'Neobvezno — zaslon ga za trenutek pokaže.',
        name_placeholder:      'Ana Novak',
        audio:                 'Deli tudi zvok',
        audio_help:            'Deluje v brskalnikih Chrome in Edge v sistemu Windows, ko delite celoten zaslon.',
        next:                  'Naprej',
        target:                'Zaslon',
        pick:                  'Izberi, kaj delite',
        pick_help:             'Brskalnik vas bo vprašal, ali delite celoten zaslon, okno ali zavihek.',
        other_network_title: 'Računalnik ni v istem omrežju kot zaslon',
        other_network_help:  'Deljenje se bo verjetno povezalo brez slike. Povežite računalnik v omrežje, v katerem je zaslon (na primer Wi-Fi sejne sobe), in vnesite kodo znova.',
        back:                  'Nazaj',
        connecting:            'Povezujem z zaslonom …',
        connecting_help:       'Zaslon sprejme sliko v nekaj sekundah.',
        cancel:                'Prekliči',
        live_title:            'Delite na zaslon »{screen}«',
        live_elapsed:          'Deljenje traja {time}',
        live_audio:            'z zvokom',
        live_hint:             'To okno lahko pomanjšate, deljenje teče naprej. Ustavite ga tukaj ali z gumbom brskalnika.',
        preview:               'Predogled tega, kar delite',
        stop:                  'Ustavi deljenje',
        replace:               'Zamenjaj vsebino',
        ended_title:           'Deljenje je končano',
        ended_problem_title:   'Deljenje se je prekinilo',
        again:                 'Deli znova',
        end_sender:            'Zaslon spet prikazuje svojo vsebino.',
        end_screen:            'Zaslon je prekinil povezavo.',
        end_replaced:          'Zaslon je prevzel nekdo drug.',
        end_expired:           'Zaslon se ni odzval.',
        end_disabled:          'Deljenje na tem zaslonu je izklopljeno.',
        end_no_connection:     'Povezava do zaslona ni uspela. Računalnik in zaslon morata biti v istem omrežju.',
        end_connection_lost:   'Povezava z zaslonom se je prekinila.',
        end_server_lost:       'Strežnik ni dosegljiv, zato je deljenje prekinjeno.',
        end_gone:              'Tega deljenja ni več.',
        end_failed:            'Deljenja ni bilo mogoče začeti.',
        error_code:            'Koda ne obstaja ali deljenje na tem zaslonu ni vklopljeno.',
        error_throttled:       'Preveč napačnih kod. Poskusite znova čez nekaj minut.',
        error_tenant_inactive: 'Ta prostor je izklopljen, zato deljenje ni mogoče.',
        error_network:         'Strežnik ni dosegljiv. Preverite povezavo z omrežjem in poskusite znova.',
        error_open:            'Deljenja ni bilo mogoče začeti.',
        capture_os_denied:     'macOS brskalniku ne dovoli snemanja zaslona. Dovolite ga v Sistemskih nastavitvah → Zasebnost in varnost → Snemanje zaslona, nato brskalnik znova zaženite.',
        capture_no_audio:      'Ta brskalnik ne deli zvoka. Počistite »Deli tudi zvok« in poskusite znova.',
        capture_failed:        'Zajem zaslona ni uspel. Poskusite znova.',
        unsupported_title:     'Delite iz brskalnika na računalniku',
        unsupported:           'Odprite to stran v brskalniku Chrome, Edge, Firefox ali Safari na prenosniku ali namiznem računalniku.',
        unsupported_airplay:   'Z iPhona, iPada ali Maca lahko zaslon delite tudi neposredno prek AirPlaya na Apple TV.',
        insecure_title:        'Stran ni odprta varno',
        insecure:              'Brskalnik deli zaslon le s strani, odprte prek https. Odprite {url}.',
    },
    pending: {
        title:               'Čakajo na potrditev ({n})',
        badge:               'Čaka na potrditev: {n}',
        badge_open:          'Odpri zaslone',
        code_on_screen:      'koda na zaslonu',
        ip:                  'Naslov IP, s katerega se je oglasil zaslon',
        unknown_device:      'Neznana naprava',
        no_tenant:           'Brez prostora',
        same_network:        'Isto omrežje',
        same_network_help:   'Zaslon je odprl /tv na istem omrežju, s katerega dela skrbnik tega prostora.',
        confirm:             'Potrdi',
        dismiss:             'Zavrni',
        dismissed:           'Zavrnjeno — zaslon bo pokazal novo kodo.',
        confirm_title:       'Potrdi zaslon',
        confirm_failed:      'Potrditev ni uspela.',
        confirmed:           'Zaslon je dodan in seznanjen.',
        confirmed_in:        'Zaslon je dodan in seznanjen v prostoru {tenant}.',
        tenant:              'Prostor',
        other_tenant_hint:   'Skupino in osnovno vsebino zaslona nastavite v prostoru {tenant}.',
        mode_new:            'Nov zaslon',
        mode_existing:       'Obstoječ zaslon',
        existing_screen:     'Zaslon',
        existing_pick:       'Izberite zaslon',
        existing_unpaired:   'Neseznanjeni',
        existing_paired:     'Seznanjeni (prikazovalnik bo zamenjan)',
        default_name:        'Zaslon',
        link:                'Povezava za seznanitev',
        short_help:          'Na zaslonu odprite {address} (brez https://) — zaslon se bo sam pojavil tukaj, če ste na istem omrežju.',
        link_or:             'Ali pa povezava s prostorom:',
        link_manual:         'Zaslon lahko dodate tudi ročno in vpišete kodo, ki jo prikaže.',
        copy:                'Kopiraj',
        copied:              'Kopirano',
        copy_failed:         'Kopiranje ni uspelo — označite povezavo in jo kopirajte ročno.',
    },
    media: {
        title:              'Vsebine',
        subtitle:           'Slike in videi, ki jih uporabljajo vaše postavitve.',
        upload:             'Naloži',
        drop:               'Spustite datoteke za nalaganje',
        drop_help:          'Povlecite slike in videe sem ali kliknite »Naloži«. Slike JPEG, PNG, WebP, SVG do 20 MB · videi MP4, WebM do 300 MB.',
        filter_all:         'Vse',
        filter_image:       'Slike',
        filter_video:       'Videi',
        kind_image:         'Slika',
        kind_video:         'Video',
        uploading:          'Nalagam {name} …',
        uploaded:           'Naložena je {n} datoteka | Naloženi sta {n} datoteki | Naložene so {n} datoteke | Naloženih je {n} datotek',
        upload_failed_named: 'Datoteke »{name}« ni bilo mogoče naložiti.',
        too_large:          'Datoteka je prevelika (slike do 20 MB, videi do 300 MB).',
        too_large_named:    '»{name}« je prevelika — {kind} je lahko velika največ {limit}.',
        unsupported:        'Te vrste datoteke ni mogoče uporabiti.',
        unsupported_named:  '»{name}«: podprte so slike JPEG, PNG, WebP in SVG ter videi MP4 in WebM.',
        rename:             'Preimenuj',
        rename_title:       'Preimenuj datoteko',
        name:               'Ime',
        delete:             'Izbriši datoteko',
        confirm_delete:     'Izbrišem »{name}«?',
        deleted:            'Datoteka je izbrisana.',
        empty:              'Ni še nobene datoteke.',
        empty_filtered:     'Ni datotek te vrste.',
        uploaded_by:        '{name}, {date}',
        dimensions:         '{width} × {height}',
    },
    templates: {
        title:               'Predstavitve',
        subtitle:            'Postavitve, zaporedja in predloge v barvah vaše znamke.',
        tab_designs:         'Postavitve',
        tab_playlists:       'Zaporedja',
        tab_templates:       'Predloge',
        new_design:          'Nova postavitev',
        yours:               'Vaše postavitve',
        yours_subtitle:      'Postavitve, ki jih prikazujejo vaši zasloni in urniki.',
        yours_empty:         'Ni še nobene postavitve. Začnite s predlogo v zavihku Predloge.',
        start:               'Začnite s predlogo',
        start_subtitle:      'Predloge so v barvah in pisavi vaše znamke; vse lahko nato poljubno uredite.',
        blank:               'Prazna postavitev',
        blank_help:          'Prazno platno v barvi podlage vaše znamke.',
        edit:                'Uredi',
        duplicate:           'Podvoji',
        duplicate_title:     'Podvoji postavitev',
        copy_of:             '{name} (kopija)',
        delete:              'Izbriši postavitev',
        confirm_delete:      'Izbrišem postavitev »{name}«?',
        deleted:             'Postavitev je izbrisana.',
        name:                'Ime postavitve',
        name_placeholder:    'Recepcija — dobrodošlica',
        new_title:           'Nova postavitev',
        new_from:            'Iz predloge »{name}«',
        create:              'Ustvari in uredi',
        created:             'Postavitev je ustvarjena.',
        published:           'Objavljeno · v{version}',
        unpublished_changes: 'Neobjavljene spremembe',
        never_published:     'Ni objavljeno',
        updated:             'Spremenjeno {ago}',
        in_use:              'Na {screens} zaslonih · v {schedules} pravilih',
    },
    playlist: {
        title:              'Zaporedja',
        subtitle:           'Več strani, ki se na zaslonu izmenjujejo — vsaka svoj čas, z izbranim prehodom.',
        empty:              'Ni še nobenega zaporedja. Z njim zaslon izmenjuje več postavitev.',
        new:                'Novo zaporedje',
        edit:               'Uredi',
        duplicate:          'Podvoji',
        duplicate_title:    'Podvoji zaporedje',
        copy_of:            '{name} (kopija)',
        delete:             'Izbriši zaporedje',
        confirm_delete:     'Izbrišem zaporedje »{name}«?',
        deleted:            'Zaporedje je izbrisano.',
        name:               'Ime zaporedja',
        name_placeholder:   'Nebotičnik — zgodovina',
        default_name:       'Novo zaporedje',
        pages:              '{n} stran | {n} strani | {n} strani | {n} strani',
        seconds:            '{n} s',
        minutes:            '{m} min',
        minutes_seconds:    '{m} min {s} s',
        hours:              '{h} h',
        hours_minutes:      '{h} h {m} min',
        skipped:            '{n} neobjavljena stran se preskoči | {n} neobjavljeni strani se preskočita | {n} neobjavljene strani se preskočijo | {n} neobjavljenih strani se preskoči',
        nothing_playable:   'Nobena stran ni objavljena — zasloni zaporedja ne bodo prikazali.',
        editor_title:       'Zaporedje',
        pages_title:        'Strani',
        pages_subtitle:     'Po vrsti, kot se prikazujejo. Ista postavitev je lahko v zaporedju tudi večkrat.',
        pages_empty:        'Zaporedje še nima strani. Dodajte postavitve, ki naj se izmenjujejo.',
        add_page:           'Dodaj stran',
        page_number:        'Stran {n}',
        unpublished_skipped: 'Neobjavljena — se preskoči',
        design_gone:        '(izbrisana postavitev)',
        duration:           'Trajanje',
        duration_unit:      's',
        duration_rule:      'Trajanje je od 3 do 3600 sekund.',
        move_up:            'Premakni gor',
        move_down:          'Premakni dol',
        remove_page:        'Odstrani stran',
        open_design:        'Uredi postavitev',
        total:              'Skupaj',
        settings:           'Nastavitve',
        transition:         'Prehod',
        in_use:             'Uporaba',
        used_screens:       'Na {n} zaslonu | Na {n} zaslonih | Na {n} zaslonih | Na {n} zaslonih',
        used_rules:         'v {n} pravilu | v {n} pravilih | v {n} pravilih | v {n} pravilih',
        not_used:           'Ni še na nobenem zaslonu ali v pravilu.',
        preview:            'Predogled',
        preview_unavailable: 'Predogled še ni na voljo.',
        preview_nothing:    'Za predogled mora biti objavljena vsaj ena stran.',
        preview_close:      'Zapri predogled',
        saved:              'Zaporedje je shranjeno.',
        created:            'Zaporedje je ustvarjeno.',
        unsaved:            'Neshranjene spremembe',
        all_saved:          'Shranjeno',
        leave_title:        'Zapustim urejanje?',
        leave_message:      'Sprememb zaporedja še niste shranili. Če zapustite stran, se izgubijo.',
        leave_confirm:      'Zavrzi spremembe',
        items_required:     'Dodajte vsaj eno stran.',
        items_max:          'Zaporedje ima lahko največ 50 strani.',
        load_failed:        'Zaporedja ni bilo mogoče naložiti.',
        picker_title:       'Dodaj strani',
        picker_subtitle:    'Izberite postavitve — dodane bodo na konec zaporedja, vsaka za {n} s.',
        picker_add:         'Dodaj izbrane ({n})',
        picker_empty:       'Ni še nobene postavitve. Najprej jo ustvarite v Predstavitvah.',
        picker_room:        'Dodate lahko še {n}.',
        picker_none_found:  'Nobena postavitev ne ustreza iskanju.',
        add_to:             'Dodaj v zaporedje',
        add_to_subtitle:    '»{name}« bo dodana na konec zaporedja.',
        add_to_target:      'Zaporedje',
        add_to_new:         'Novo zaporedje s to stranjo',
        add_to_full:        'Zaporedje je polno (50 strani).',
        added:              'Stran je dodana v »{name}«.',
        group_designs:      'Postavitve',
        group_playlists:    'Zaporedja',
        playlist_named:     'Zaporedje: {name}',
        playing_in:         '{page} ({playlist})',
    },
    transition: {
        fade:       'Pretapljanje',
        cut:        'Rez',
        slide:      'Drsenje',
        fade_desc:  'Stran se mehko pretopi v naslednjo.',
        cut_desc:   'Naslednja stran se pokaže takoj.',
        slide_desc: 'Naslednja stran pridrsi z desne.',
    },
    style: {
        calm:          'Umirjeno',
        contrast:      'Kontrastno',
        minimal:       'Minimalno',
        calm_desc:     'Naravno, elegantno, brezčasno.',
        contrast_desc: 'Drzno, sodobno, učinkovito.',
        minimal_desc:  'Čisto, moderno, osredotočeno.',
    },
    schedule: {
        title:               'Urniki',
        subtitle:            'Pravila objave določajo, kje in kdaj se prikazuje katera vsebina.',
        rules:               'Pravila objave',
        rules_subtitle:      'Razvrščena po prednosti — ko se pravila prekrivajo, velja tisto z manjšo številko.',
        add:                 'Novo pravilo',
        edit:                'Uredi pravilo',
        delete:              'Izbriši pravilo',
        form_subtitle:       'Določite, kje in kdaj se prikazuje vaša vsebina.',
        name:                'Ime pravila',
        name_placeholder:    'Zajtrk',
        targets:             'Zasloni',
        targets_placeholder: 'Izberite zaslone ali skupine',
        targets_groups:      'Skupine',
        targets_screens:     'Zasloni',
        targets_required:    'Izberite vsaj en zaslon ali skupino.',
        days:                'Dnevi',
        days_required:       'Izberite vsaj en dan.',
        time:                'Čas prikaza',
        from:                'Od',
        to:                  'Do',
        same_time:           'Začetek in konec ne smeta biti enaka.',
        midnight:            'Čez polnoč: prikaz se konča naslednji dan ob {to}.',
        midnight_short:      'čez polnoč',
        dates:               'Obdobje',
        dates_help:          'Neobvezno: pravilo velja samo med tema dnevoma (vključno).',
        dates_order:         'Konec obdobja ne sme biti pred začetkom.',
        date_from:           'Od dne',
        date_to:             'Do dne',
        date_open:           'brez omejitve',
        content:             'Vsebina',
        design_unpublished:  'Ta postavitev še ni objavljena — zasloni je ne bodo prikazali, dokler je ne objavite.',
        after_end:           'Po koncu',
        priority:            'Prednost',
        priority_help:       'Od 0 do 1000; manjša številka ima prednost, ko se pravila prekrivajo.',
        active:              'Aktivno',
        confirm_delete:      'Izbrišem pravilo »{name}«?',
        deleted:             'Pravilo je izbrisano.',
        empty:               'Ni še nobenega pravila.',
        empty_help:          'Brez pravil zasloni prikazujejo svojo osnovno vsebino.',
        no_designs:          'Najprej ustvarite postavitev v Predstavitvah.',
        activated:           'Pravilo »{name}« je vklopljeno.',
        deactivated:         'Pravilo »{name}« je izklopljeno.',
        unknown:             '(izbrisano)',
        every_day:           'Vsak dan',
        trigger:             'Kdaj',
        price_list:          'Cenik',
        price_list_placeholder: 'Izberite cenik',
        price_list_required: 'Izberite cenik.',
        price_list_note:     'Pravilo teče, ko je cenik v blagajni aktiven.',
        price_list_no_menu:  'Blagajna še ni povezana — najprej jo povežite v Meniju.',
        price_list_none:     'V blagajni ni nobenega cenika poleg privzetega.',
        by_price_list:       'Po ceniku: {name} ({schedule})',
        by_price_list_gone:  'Po ceniku, ki ga v blagajni ni več',
    },
    weekday: {
        d1: 'Pon',
        d2: 'Tor',
        d3: 'Sre',
        d4: 'Čet',
        d5: 'Pet',
        d6: 'Sob',
        d7: 'Ned',
    },
    after_end: {
        default: 'Osnovna vsebina',
        off:     'Izklopljen zaslon',
    },
    branding: {
        title:              'Znamka in prikaz',
        subtitle:           'Prilagodite videz zaslonov svoji blagovni znamki in prostoru.',
        save:               'Shrani spremembe',
        saved:              'Spremembe so shranjene.',
        discard:            'Zavrzi',
        unsaved:            'Neshranjene spremembe',
        identity:           'Celostna podoba',
        identity_subtitle:  'Določite, kako bo vaša znamka videti na zaslonih.',
        logo:               'Logotip',
        logo_upload:        'Naloži logotip',
        logo_help:          'PNG, SVG ali JPG (najv. 5 MB)',
        logo_replace:       'Zamenjaj',
        logo_remove:        'Odstrani',
        logo_too_large:     'Logotip je večji od 5 MB.',
        logo_type:          'Logotip mora biti PNG, SVG ali JPG.',
        logo_failed:        'Logotipa ni bilo mogoče naložiti.',
        colors:             'Barve',
        color_0:            'Glavna',
        color_1:            'Podlaga',
        color_2:            'Poudarek',
        color_pick:         'Izberi barvo: {name}',
        color_invalid:      'Barva mora biti v obliki #RRGGBB.',
        font:               'Pisava',
        domain:             'Spletni naslov',
        domain_placeholder: 'zasloni.vasa-domena.si',
        domain_invalid:     'Vpišite ime gostitelja, npr. zasloni.hotel.si.',
        provider_brand:     'Prikaži znamko ponudnika',
        provider_brand_off: 'Na zaslonih ne bo prikazan logotip ponudnika.',
        provider_brand_on:  'Na zaslonih bo diskretno prikazana znamka ponudnika.',
        location:           'Lokacija',
        location_subtitle:  'Za vreme na zaslonih, ki nimajo svoje lokacije.',
        preview:            'Predogled v živo',
        preview_subtitle:   'Tako bo vaša vsebina videti na zaslonih.',
        preview_screen:     'Zaslon za predogled',
        pick_template:      'Izberi predlogo',
        pick_template_help: 'Hitro prilagodite videz s pripravljeno predlogo.',
        layout:             'Postavitev',
        layout_help:        'Izberite usmerjenost prikaza za nove vsebine.',
        read_only:          'Znamko ureja skrbnik stranke — vi jo lahko samo pogledate.',
    },
    look: {
        title:                    'Videzi',
        subtitle:                 'Druge znamke v istem prostoru — npr. sejna soba, ki jo uporablja več podjetij. Zaslon preklopite nanje pod Zasloni.',
        default:                  'Osnovni videz',
        default_help:             'Osnovni videz je celostna podoba zgoraj.',
        add:                      'Nov videz',
        edit:                     'Uredi videz',
        delete:                   'Izbriši videz',
        confirm_delete:           'Izbrišem videz {name}? Zasloni in sestanki, ki ga uporabljajo, se vrnejo na osnovni videz.',
        deleted:                  'Videz je izbrisan.',
        added:                    'Videz je dodan.',
        saved:                    'Videz je shranjen.',
        empty_help:               'Dodajte videz za vsako podjetje, ki uporablja prostor: logotip, barve in pisavo.',
        limit:                    'Največ {n} videzov.',
        name:                     'Ime',
        name_placeholder:         'IT TEAM',
        logo:                     'Logotip',
        logo_pick:                'Iz Vsebin',
        logo_upload:              'Naloži',
        logo_none:                'Brez logotipa',
        share_domain:             'Naslov za deljenje zaslona',
        share_domain_placeholder: 'deli.vasa-domena.si',
        share_domain_help:        'Zaslon v tem videzu za deljenje pokaže ta naslov (…/deli). Pot do strežnika (edge) in certifikat za naslov nastavi ponudnik ročno.',
        preview:                  'Predogled',
        select:                   'Videz zaslona',
        changed:                  'Zaslon {name} je zdaj v videzu {look}.',
    },
    service_pin: {
        button:        'Servisna koda',
        title:         'Servisna koda zaslonov',
        is_set:        'Servisna koda je nastavljena. Iz varnostnih razlogov je ni mogoče prikazati — lahko jo zamenjate ali odstranite.',
        not_set:       'Servisna koda ni nastavljena: servisni meni se na seznanjenih zaslonih ne odpre.',
        badge_set:     'nastavljena',
        badge_not_set: 'ni nastavljena',
        help_open:     'Koda odpre servisni meni v aplikaciji Imprion DS za Apple TV: na daljinskem upravljalniku 3 sekunde držite tipko Predvajaj/Premor.',
        help_without:  'Brez kode se servisni meni na seznanjenih zaslonih ne odpre.',
        help_tvos:     'Koda ne odklene nastavitev sistema tvOS — le servisni meni aplikacije.',
        pin:           'Koda',
        new_pin:       'Nova koda',
        pin_help:      '4 do 8 števk. Velja za vse zaslone tega prostora.',
        show:          'Pokaži kodo',
        hide:          'Skrij kodo',
        save:          'Nastavi kodo',
        change:        'Zamenjaj kodo',
        remove:        'Odstrani kodo',
        confirm_remove: 'Odstranim servisno kodo? Servisni meni se na seznanjenih zaslonih ne bo več odprl, dokler ne nastavite nove.',
        saved:         'Servisna koda je nastavljena.',
        removed:       'Servisna koda je odstranjena — servisni meni se na seznanjenih zaslonih ne odpre več.',
        invalid:       'Koda mora imeti od 4 do 8 števk.',
        failed:        'Servisne kode ni bilo mogoče shraniti.',
    },
    meeting: {
        button:            'Sestanek',
        title:             'Sestanek · {name}',
        description:       'Zaslon do konca sestanka pokaže izbrano vsebino in pozdravi gosta.',
        guest:             'Gost',
        guest_placeholder: 'Naturalica',
        guest_help:        "Ime se pokaže namesto {'{gost}'} v vsebini.",
        guest_logo:        'Logotip gosta',
        logo_pick:         'Iz Vsebin',
        logo_upload:       'Naloži',
        logo_remove:       'Odstrani logotip gosta',
        content:           'Vsebina',
        content_none:      'Ni objavljene vsebine — objavite postavitev v urejevalniku.',
        content_required:  'Izberite, kaj zaslon pokaže.',
        look:              'Videz med sestankom',
        look_screen:       'Kot zaslon ({name})',
        duration:          'Trajanje',
        durations: {
            '1h':         '1 h',
            '2h':         '2 h',
            '4h':         '4 h',
            end_of_day:   'Do konca dneva',
            custom:       'Do ure …',
            keep:         'Kot zdaj (do {time})',
        },
        custom_end:        'Konec ob',
        custom_invalid:    'Vpišite uro, npr. 16:30.',
        ends:              'Konec: {when}',
        preview:           'Predogled',
        start:             'Začni sestanek',
        update:            'Posodobi sestanek',
        started:           'Sestanek se je začel — zaslon ga pokaže v pol minute.',
        updated:           'Sestanek je posodobljen.',
        start_failed:      'Sestanka ni bilo mogoče začeti.',
        end:               'Končaj',
        ended:             'Sestanek je končan.',
        end_failed:        'Sestanka ni bilo mogoče končati.',
        until:             'do {time}',
        running:           'Sestanek · {until}',
        running_named:     'Sestanek: {guest} · {until}',
        // A meeting's own canvas ("Prilagodi za ta sestanek", docs/API.md).
        quick_title:               'Besedila in slike',
        quick_help:                'Spremembe veljajo samo za ta sestanek — postavitev ostane, kot je.',
        quick_empty:               'Ta postavitev nima besedil ali slik. Vse drugo spremenite s »Prilagodi za ta sestanek«.',
        customized:                'Prilagojeno',
        hide:                      'Skrij',
        show:                      'Pokaži',
        hidden:                    'Skrito',
        guest_token_hint:          'Namesto {token} zaslon pokaže ime gosta.',
        role_brand_note:           'Logotip znamke — sliko določa videz med sestankom.',
        role_guest_note:           'Logotip gosta — sliko izberete zgoraj pri »Logotip gosta«.',
        image_failed:              'Slike ni bilo mogoče naložiti.',
        reset:                     'Ponastavi',
        reset_title:               'Ponastavim prilagoditev?',
        reset_message:             'Spremembe za ta sestanek bodo zavržene — zaslon pokaže postavitev, kot je objavljena.',
        content_change_title:      'Zamenjam vsebino?',
        content_change:            'Spremembe za ta sestanek ne veljajo za drugo vsebino in bodo zavržene.',
        content_change_confirm:    'Zamenjaj',
        customize:                 'Prilagodi za ta sestanek',
        customize_title:           'Prilagodi za ta sestanek',
        customize_subtitle:        '{name} — spremembe veljajo samo za ta sestanek',
        apply:                     'Uporabi',
        customize_discard_title:   'Zavržem spremembe?',
        customize_discard:         'Spremembe v urejevalniku bodo zavržene; sestanek ostane, kot je bil.',
        customize_discard_confirm: 'Zavrzi',
        save_design:               'Shrani kot postavitev',
        save_design_title:         'Shrani kot postavitev',
        save_design_description:   'Prilagoditev postane nova objavljena postavitev v Predstavitvah, sestanek pa jo prikazuje.',
        save_design_name:          '{design} — {guest}',
        save_design_name_copy:     '{design} — sestanek',
        save_design_failed:        'Postavitve ni bilo mogoče shraniti.',
        design_saved:              'Shranjeno kot postavitev »{name}« — sestanek jo prikazuje.',
    },
    menu: {
        title:                   'Meni',
        subtitle:                'Artikli iz blagajne Imprion POS, pripravljeni za prikaz na zaslonih.',
        sync:                    'Osveži iz blagajne',
        synced:                  'Osveženo iz blagajne — dodanih: {added}, posodobljenih: {updated}, odstranjenih: {removed}.',
        sync_failed:             'Osvežitev iz blagajne ni uspela.',
        connection:              'Povezava z blagajno',
        last_sync:               'Zadnja sinhronizacija: {ago}',
        never_synced:            'Meni še ni bil naložen iz blagajne.',
        last_error:              'Zadnja sinhronizacija ni uspela: {error}',
        products_count:          '{n} artikel | {n} artikla | {n} artikli | {n} artiklov',
        auto_every:              'samodejno na {n} min',
        auto_off:                'samodejna sinhronizacija je izklopljena',
        not_connected:           'Blagajna še ni povezana',
        not_connected_help:      'Povežite blagajno Imprion POS: artikli, kategorije in ceniki se naložijo sami, na zaslonih pa jih prikažete z elementoma Meni in Happy hour.',
        not_connected_editor:    'Skrbnik objekta mora povezati blagajno.',
        connect:                 'Poveži blagajno',
        not_synced:              'Meni še ni naložen',
        not_synced_help:         'Blagajna je povezana. Kliknite »Osveži iz blagajne«, da se artikli naložijo.',
        tab_products:            'Artikli',
        tab_categories:          'Kategorije',
        tab_price_lists:         'Ceniki (Happy hour)',
        search:                  'Išči po imenu ali šifri …',
        filter_all:              'Vsi',
        filter_hidden:           'Skriti',
        filter_happy:            'S happy hour ceno',
        filter_removed:          'Odstranjeni',
        empty_products:          'V blagajni ni artiklov.',
        empty_filtered:          'Noben artikel ne ustreza izbiri.',
        no_category:             'Brez kategorije',
        category_hidden:         'Skrita na zaslonih',
        hidden:                  'Skrij na zaslonih',
        show_on_screens:         'Prikaži na zaslonih',
        hidden_badge:            'Skrit',
        removed:                 'Ni več v blagajni',
        disabled:                'Onemogočen v blagajni',
        product_hidden:          '»{name}« je skrit na zaslonih.',
        product_shown:           '»{name}« je spet prikazan na zaslonih.',
        edit_product:            'Uredi artikel',
        edit_product_subtitle:   'Prikaz na zaslonih — imena in cene v blagajni ostanejo, kot so.',
        display_name:            'Prikazno ime',
        display_name_help:       'Prazno = ime iz blagajne.',
        description:             'Opis',
        image:                   'Slika',
        image_pick:              'Izberi iz Vsebin',
        image_replace:           'Zamenjaj',
        image_remove:            'Odstrani',
        image_none:              'Brez slike',
        framing:                 'Kadriranje slike',
        framing_help:            'Povlecite sliko, da izberete, kateri del ostane viden v okvirju menija; povečava jo približa.',
        framing_drag:            'Premakni sliko v okvirju (puščice)',
        framing_value:           'Vodoravno {x} %, navpično {y} %',
        framing_shapes:          'V oblikah',
        framing_zoom:            'Povečava',
        framing_reset:           'Ponastavi',
        tags:                    'Oznake',
        position:                'Vrstni red',
        position_help:           'Manjša številka je višje v svoji kategoriji; enake si sledijo po imenu.',
        pos_facts:               'Podatki iz blagajne',
        pos_facts_help:          'Te podatke ureja blagajna — osvežijo se ob vsaki sinhronizaciji.',
        pos_name:                'Ime v blagajni',
        code:                    'Šifra',
        price:                   'Cena',
        vat:                     'DDV',
        category:                'Kategorija',
        list_prices:             'Cene po cenikih',
        list_prices_none:        'Ni na nobenem dodatnem ceniku.',
        product_saved:           'Artikel je shranjen.',
        categories_empty:        'V blagajni ni kategorij.',
        category_pos_name:       'V blagajni: {name}',
        category_display_name:   'Prikazno ime kategorije',
        category_saved:          'Kategorija je shranjena.',
        category_hidden_toast:   'Kategorija »{name}« je skrita na zaslonih.',
        category_shown_toast:    'Kategorija »{name}« je spet prikazana na zaslonih.',
        order_saved:             'Vrstni red je shranjen.',
        move_up:                 'Premakni gor',
        move_down:               'Premakni dol',
        price_lists_help:        'Happy hour urnike nastavite v blagajni (back office → Ceniki). Zasloni jih preberejo ob vsaki sinhronizaciji.',
        price_lists_empty:       'V blagajni ni cenikov.',
        default_list:            'Privzeti',
        default_list_help:       'Osnovne cene — veljajo, ko noben drug cenik ni aktiven.',
        list_active:             'Vklopljen v blagajni',
        active_now:              'Zdaj aktiven',
        priority:                'Prednost {n}',
        priority_help:           'Ko je aktivnih več cenikov, velja tisti z višjo prednostjo.',
        mapping_note:            'Branje ur iz blagajne še preverjamo. Če se urnik tu ne ujema z blagajno, pošljite podpori spodnje podatke.',
        raw_conditions:          'Podatki iz blagajne',
        every_day:               'vsak dan',
        all_day:                 'ves dan',
        date_open:               'brez omejitve',
        weekday: {
            d1: 'pon',
            d2: 'tor',
            d3: 'sre',
            d4: 'čet',
            d5: 'pet',
            d6: 'sob',
            d7: 'ned',
        },
        connection_subtitle:     'Zasloni iz blagajne Imprion POS samo berejo — v blagajno nikoli ne pišejo.',
        environment:             'Okolje',
        token:                   'Žeton API',
        token_placeholder:       'Prilepite žeton API',
        token_kept:              'Žeton je shranjen — pustite prazno, da ostane.',
        token_required:          'Vpišite žeton API.',
        business_unit:           'Poslovna enota',
        business_unit_placeholder: 'Najprej naložite poslovne enote',
        business_unit_pick:      'Izberite poslovno enoto',
        business_unit_required:  'Izberite poslovno enoto.',
        business_units_load:     'Naloži poslovne enote',
        business_units_loaded:   'Poslovne enote so naložene.',
        business_units_none:     'Blagajna ni vrnila nobene poslovne enote.',
        business_units_failed:   'Poslovnih enot ni bilo mogoče naložiti.',
        auto_sync:               'Samodejna sinhronizacija',
        auto_sync_help:          'Meni se na izbrani interval sam osveži iz blagajne.',
        interval:                'Interval',
        minutes:                 '{n} min',
        connection_saved:        'Povezava z blagajno je shranjena.',
    },
    menu_tag: {
        new:         'Novo',
        recommended: 'Priporočamo',
        vegan:       'Vegansko',
        vegetarian:  'Vegetarijansko',
        spicy:       'Pikantno',
        gluten_free: 'Brez glutena',
        local:       'Lokalno',
    },
    pos_environment: {
        production: 'Produkcija',
        demo:       'Demo',
    },
    rule_trigger: {
        time:       'Ob urah',
        price_list: 'Po ceniku iz blagajne',
    },
    design: designSl,
}

export type MessageSchema = typeof sl

export default sl
